import React from 'react';
import styled from 'styled-components';
import { CreditOfferTypes, Take3CreditOffer, BnplCreditOffer, StcCreditOffer } from 'types/CreditOfferTypes';
import { TypographyComponent, BrandLogos, MarketingCard as MarketingCardComponent } from '@tvg/fuse-design';

import { renderContent, renderContentWithParams } from 'utilities/content/content-utilities';
import { sendAnalyticsEvent } from 'utilities/veryPay/veryPayUtilities';

const StyledMarketingCard = styled(MarketingCardComponent)`
  margin-bottom: var(--spacing-space24);
`;

const StcHeaderContainer = styled.span`
  > * {
    vertical-align: middle;
  }

  img {
    position: relative;
    top: 1px; // Align the Very Pay brand logo with the text
  }
`;

const StcHeader = styled(TypographyComponent)`
  margin-right: var(--spacing-space4);
`;

const Apr = styled(TypographyComponent)`
  color: var(--color-global-black);

  span {
    white-space: nowrap; // Prevent % and APR from appearing on separate lines
  }
`;

const SubHeader = styled(TypographyComponent)`
  margin-bottom: var(--spacing-space8) !important;
  display: block;
`;

type Props = {
  creditOffer: Take3CreditOffer | BnplCreditOffer | StcCreditOffer;
  nonCreditAccountUser: boolean;
  apr: string;
  onClickCallback: () => void;
};

const getCardDescription = (creditOffer: Props['creditOffer']): string => {
  switch (creditOffer.type) {
    case CreditOfferTypes.TAKE3:
      return 'verypay.card.take3.description';
    case CreditOfferTypes.BNPL:
      return 'verypay.card.bnpl.description';
    case CreditOfferTypes.STC:
      return 'verypay.card.stc.description';
    default:
      return '';
  }
};

const getCardParams = (creditOffer: Props['creditOffer']): string[] => {
  switch (creditOffer.type) {
    case CreditOfferTypes.TAKE3:
      return [creditOffer.attributes.firstPayment];
    default:
      return [];
  }
};

const getCardLink = (creditOffer: Props['creditOffer']): string => {
  switch (creditOffer.type) {
    case CreditOfferTypes.TAKE3:
      return 'verypay.card.take3.link';
    case CreditOfferTypes.BNPL:
      return 'verypay.card.bnpl.link';
    case CreditOfferTypes.STC:
      return 'verypay.card.stc.link';
    default:
      return '';
  }
};

const getAnalyticsLabel = (creditOffer: Props['creditOffer']): string => {
  switch (creditOffer.type) {
    case CreditOfferTypes.TAKE3:
      return 'Take 3 moreinfo';
    case CreditOfferTypes.BNPL:
      return 'BNPL moreinfo';
    case CreditOfferTypes.STC:
      return 'STC moreinfo';
    default:
      return '';
  }
};

function VeryPayCard(props: Props) {
  const { creditOffer, nonCreditAccountUser, apr, onClickCallback } = props;
  const isStcPrimary = creditOffer.type === CreditOfferTypes.STC;

  const renderHeader = () => {
    if (isStcPrimary) {
      return (
        <StcHeaderContainer>
          <StcHeader
            size="2"
            tag="span"
            dangerouslySetInnerHTML={{
              __html: renderContentWithParams({
                id: getCardDescription(creditOffer),
                params: getCardParams(creditOffer),
              }),
            }}
          />
          <BrandLogos brand="VeryPay" width="72" alt="Very Pay" />
        </StcHeaderContainer>
      );
    }

    return <BrandLogos brand="VeryPay" width="88" alt="Very Pay" />;
  };

  const renderSubHeader = () => {
    if (isStcPrimary) return null;

    return (
      <SubHeader
        size="2"
        tag="span"
        dangerouslySetInnerHTML={{
          __html: renderContentWithParams({
            id: getCardDescription(creditOffer),
            params: getCardParams(creditOffer),
          }),
        }}
      />
    );
  };

  const renderAprMessage = () => {
    if (isStcPrimary || !nonCreditAccountUser) return null;

    return (
      <Apr
        size="2"
        type="paragraph"
        tag="span"
        dangerouslySetInnerHTML={{
          __html: renderContentWithParams({
            id: 'verypay.card.apr',
            params: [apr],
          }),
        }}
        data-testid="verypay_card_apr"
      />
    );
  };

  const handleOnClick = (event: MouseEvent) => {
    event.preventDefault();
    onClickCallback();
    sendAnalyticsEvent({
      gaLabel: getAnalyticsLabel(creditOffer),
    });
  };

  return (
    <StyledMarketingCard
      data-testid="verypay_card"
      header={renderHeader()}
      subHeader={renderSubHeader()}
      paragraphText={renderAprMessage()}
      linkUrl="#"
      linkText={renderContent(getCardLink(creditOffer))}
      onClick={handleOnClick}
    />
  );
}

export default VeryPayCard;
